import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "@store";
import { getEmailListService, EmailListQuery } from "@/service/email.service";
import { ROLES } from "@/utils/constants";
import { selectSelectedOrganization } from "./clientOrganizationSlice";

// Types for Email list rows based on provided response sample
export type EmailRow = {
  id: string;
  client: { id: string; name: string };
  project: { id: string; name: string };
  scenario: string;
  sent_at: string | null;
  status: string;
  status_color_code: string;
};

export interface EmailListState {
  rows: EmailRow[];
  loading: boolean;
  error: string | null;
  pagination: {
    currentPage: number;
    totalPages: number;
    totalItems: number;
    pageSize: number;
  };
}

const initialState: EmailListState = {
  rows: [],
  loading: false,
  error: null,
  pagination: {
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    pageSize: 10,
  },
};

export const fetchEmails = createAsyncThunk(
  "emails/fetchEmails",
  async (
    query: EmailListQuery | undefined,
    { dispatch, getState }
  ) => {
    try {
      dispatch(setLoading(true));
      const state = getState() as RootState;
      const currentQuery: EmailListQuery = {
        page: query?.page ?? state.emails.pagination.currentPage ?? 1,
        pageSize: query?.pageSize ?? state.emails.pagination.pageSize ?? 10,
        search: query?.search,
        projectId: query?.projectId,
        clientId: query?.clientId,
        scenario: query?.scenario,
        status: query?.status,
        fromDate: query?.fromDate,
        toDate: query?.toDate,
      };

      // For client users, use the selected organization's client_id
      const userRole = state.auth.user?.role_code;
      if (userRole === ROLES.CLIENT) {
        const selectedOrg = selectSelectedOrganization(state);
        if (selectedOrg?.client_id) {
          currentQuery.clientId = selectedOrg.client_id;
        }
      }

      const { data } = await getEmailListService(currentQuery);
      // Expecting shape: { page, page_size, total_pages, total_records, data: EmailRow[] }
      return {
        rows: data.data as EmailRow[],
        pagination: {
          currentPage: data.page as number,
          totalPages: data.total_pages as number,
          totalItems: data.total_records as number,
          pageSize: data.page_size as number,
        },
      };
    } catch (error: any) {
      const message = error.response?.data?.message || "Failed to fetch emails";
      dispatch(setError(message));
      throw error;
    } finally {
      dispatch(setLoading(false));
    }
  }
);

const emailSlice = createSlice({
  name: "emails",
  initialState,
  reducers: {
    setLoading(state, action: PayloadAction<boolean>) {
      state.loading = action.payload;
    },
    setError(state, action: PayloadAction<string | null>) {
      state.error = action.payload;
    },
    setPageSize(state, action: PayloadAction<number>) {
      state.pagination.pageSize = action.payload;
    },
    setCurrentPage(state, action: PayloadAction<number>) {
      state.pagination.currentPage = action.payload;
    },
    resetEmailState: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchEmails.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchEmails.fulfilled, (state, action) => {
        state.loading = false;
        state.rows = action.payload.rows;
        state.pagination = action.payload.pagination;
      })
      .addCase(fetchEmails.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to fetch emails";
      });
  },
});

export const { setLoading, setError, setPageSize, setCurrentPage, resetEmailState } =
  emailSlice.actions;

// Selectors
export const selectEmailRows = (state: RootState) => state.emails.rows;
export const selectEmailLoading = (state: RootState) => state.emails.loading;
export const selectEmailError = (state: RootState) => state.emails.error;
export const selectEmailPagination = (state: RootState) => state.emails.pagination;

export default emailSlice.reducer;
